import React from 'react';
import type { AIAgentResponse } from '@troia-v3/types';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { StatusPill, Switch } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { AgentChannelIcons, AgentScore, agentChannelIds } from './agent-visuals';
import type { AgentChannelInfo, AgentWithScore } from './agent-visuals';

/**
 * Linha de tabela de agente (view "Lista"). Padrão-lista travado (referência
 * Usuários): checkbox de seleção (1ª col) + célula multi-linha (nome+descrição)
 * + toggle inline de status + menu ••• (última col). Avatar sem foto = slate.
 */
export interface AgentListItemProps {
  agent: AIAgentResponse;
  /** id → canal (nome/providerId) pra renderizar os círculos `.ag-chs` do mockup */
  channelsById?: Map<string, AgentChannelInfo>;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onToggleStatus?: (id: string, currentStatus: string) => void;
  onDuplicate?: (id: string) => void;
}

const getInitials = (name: string) => {
  const parts = name.split(/[\s—–-]+/).filter(Boolean);
  const first = parts[0] ?? '';
  const second = parts[1] ?? '';
  if (first && second) return `${first.charAt(0)}${second.charAt(0)}`.toUpperCase();
  return name.trim().slice(0, 2).toUpperCase();
};

export const AgentListItem: React.FC<AgentListItemProps> = ({
  agent,
  channelsById,
  selected = false,
  onToggleSelect,
  onEdit,
  onDelete,
  onToggleStatus,
  onDuplicate,
}) => {
  const isActive = agent.status === 'active';
  const qualityScore = (agent as AgentWithScore).qualityScore;
  const channelIds = agentChannelIds(agent);

  const actions: CardAction[] = [];
  if (onEdit) actions.push({ label: 'Editar', icon: 'edit', onClick: () => onEdit(agent.id) });
  if (onToggleStatus) {
    actions.push({
      label: isActive ? 'Pausar' : 'Ativar',
      icon: 'power',
      onClick: () => onToggleStatus(agent.id, agent.status),
    });
  }
  if (onDuplicate) actions.push({ label: 'Duplicar', icon: 'copy', onClick: () => onDuplicate(agent.id) });
  if (onDelete) actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(agent.id), variant: 'danger', dividerBefore: true });

  return (
    <tr
      className="border-b transition-colors"
      style={{
        borderColor: 'var(--theme-card-border)',
        backgroundColor: selected ? 'var(--theme-primary-background)' : undefined,
        cursor: onEdit ? 'pointer' : 'default',
      }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      onClick={() => onEdit?.(agent.id)}
      data-testid={`agent-row-${agent.id}`}
    >
      {/* Checkbox */}
      <td className="pl-4 pr-1 py-3 w-11">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleSelect?.(agent.id); }}
          className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
          style={{
            backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
            borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
            color: '#ffffff',
          }}
          aria-label={selected ? 'Desmarcar' : 'Selecionar'}
          data-testid={`agent-check-${agent.id}`}
        >
          {selected && <Icon name="check" className="w-3 h-3" />}
        </button>
      </td>

      {/* Agente: avatar + nome + descrição */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar className="w-9 h-9 flex-shrink-0" isSquircle>
            <AvatarFallback isSquircle>
              <span className="text-white font-semibold text-xs">{getInitials(agent.name)}</span>
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
              {agent.name}
            </div>
            {agent.description && (
              <div className="text-xs truncate max-w-[280px]" style={{ color: 'var(--theme-muted-text)' }}>
                {agent.description}
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Status — F47: switch inline (Switch shared sm) + rótulo do estado */}
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        {onToggleStatus ? (
          <div
            className="inline-flex items-center gap-2"
            data-testid={`agent-toggle-${agent.id}`}
          >
            <Switch
              size="sm"
              checked={isActive}
              onChange={() => onToggleStatus(agent.id, agent.status)}
            />
            <span className="text-xs font-semibold" style={{ color: isActive ? 'var(--theme-success-fg)' : 'var(--theme-muted-text)' }}>
              {isActive ? 'Ativo' : 'Pausado'}
            </span>
          </div>
        ) : (
          <StatusPill
            variant={isActive ? 'success' : 'neutral'}
            label={isActive ? 'Ativo' : 'Pausado'}
            data-testid={`agent-status-${agent.id}`}
          />
        )}
      </td>

      {/* Modelo */}
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1.5 text-sm" style={{ color: 'var(--theme-card-text)' }}>
          <Icon name="cpu" className="w-3.5 h-3.5" style={{ color: 'var(--theme-muted-text)' }} />
          {agent.model}
        </span>
      </td>

      {/* Canais (`.ag-chs` do mockup) */}
      <td className="px-4 py-3">
        {channelsById && channelIds.length > 0 ? (
          <AgentChannelIcons channelIds={channelIds} channelsById={channelsById} />
        ) : (
          <span className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>—</span>
        )}
      </td>

      {/* Qualidade (`.ag-score` do mockup: barra 44×6 + %) */}
      <td className="px-4 py-3">
        {qualityScore !== undefined ? (
          <AgentScore score={qualityScore} />
        ) : (
          <span className="text-sm" style={{ color: 'var(--theme-muted-text)' }} title="Sem avaliações nos últimos 30 dias">—</span>
        )}
      </td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14" onClick={(e) => e.stopPropagation()}>
        <CardActionsMenu actions={actions} size="sm" data-testid={`agent-actions-${agent.id}`} />
      </td>
    </tr>
  );
};

AgentListItem.displayName = 'AgentListItem';
